import { FC, useEffect, ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRouteAccess } from '../hooks/useRouteAccess';
import { AppLoader } from '../components/appLoader/AppLoader';
import type {
  ProtectedRouteAuthState,
  ProtectedRoutesMiddlewareConfig,
  RouteAccessConfig,
} from './protected-routes.types';

const DEFAULT_LOADER_STYLE =
  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';

export interface ProtectedRoutesMiddlewareProps {
  authState: ProtectedRouteAuthState;
  config: ProtectedRoutesMiddlewareConfig;
  children: ReactNode;
  /** Affiché quand routeAccess noAccessBehavior === 'message' et pas d'accès */
  noAccessContent?: ReactNode;
}

/**
 * Middleware global pour les routes protégées.
 * Gère loading, auth, redirections custom, pas de company, et optionnellement le contrôle d'accès par route.
 */
export const ProtectedRoutesMiddleware: FC<ProtectedRoutesMiddlewareProps> = ({
  authState,
  config,
  children,
  noAccessContent,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const {
    authMode,
    authAppBaseUrl,
    appReferer,
    onClearAuth,
    onBeforeExternalRedirect,
    LoaderComponent = AppLoader,
    routeAccess,
    getCustomRedirect,
  } = config;

  const isClientApp = authMode === 'client-app';


  const routeAccessResult = useRouteAccess({
    routes: (routeAccess?.routes ?? []) as RouteAccessConfig[],
    defaultRedirect: routeAccess?.defaultRedirect ?? '/home',
    activeCompany: authState.activeCompany ?? null,
    location: pathname,
  });

  const hasAccess = routeAccess ? routeAccessResult.hasAccess : true;
  const fallbackRoute = routeAccess
    ? routeAccessResult.fallbackRoute
    : '/home';

  useEffect(() => {
    if (routeAccess && !hasAccess && fallbackRoute && authState.isAuthenticated) {
      navigate(fallbackRoute, { replace: true });
    }
  }, [hasAccess, fallbackRoute, authState.isAuthenticated, navigate, routeAccess]);

  const redirectToAuth = () => {
    if (authMode === 'auth-app') {
      return <Navigate to="/login" replace />;
    }
    onClearAuth?.();
    const url = authAppBaseUrl
      ? `${authAppBaseUrl}/login${appReferer ? `?appReferer=${appReferer}` : ''}`
      : '/login';
    window.location.href = url;
    return (
      <div className={DEFAULT_LOADER_STYLE}>
        <LoaderComponent />
      </div>
    );
  };

  if (authState.isLoading) {
    return (
      <div className={DEFAULT_LOADER_STYLE}>
        <LoaderComponent />
      </div>
    );
  }

  if (getCustomRedirect) {
    const custom = getCustomRedirect({ pathname, authState });
    if (custom) {
      if (custom.type === 'internal') {
        return (
          <Navigate
            to={custom.to}
            replace={custom.replace ?? true}
            state={custom.state}
          />
        );
      }
      onBeforeExternalRedirect?.();
      window.location.href = custom.url;
      return (
        <div className={DEFAULT_LOADER_STYLE}>
          <LoaderComponent />
        </div>
      );
    }
  }

  if (!authState.isAuthenticated || authState.isError) {
    return redirectToAuth();
  }

  if (!authState.activeCompany && isClientApp) {
    return redirectToAuth();
  }

  if (routeAccess && !hasAccess) {
    if (routeAccess.noAccessBehavior === 'message' && noAccessContent) {
      return noAccessContent;
    }
    if (routeAccess.noAccessBehavior === 'message') {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="p-8 text-center">
            <h2 className="mb-4 text-2xl font-bold text-neutral-10">
              No access
            </h2>
            <p className="mb-6 text-neutral-40">You don&apos;t have access to this page.</p>
          </div>
        </div>
      );
    }
    return (
      <div className={DEFAULT_LOADER_STYLE}>
        <LoaderComponent />
      </div>
    );
  }

  return children;
};
